import { useState, useEffect, useCallback, useRef } from "react";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  StickyNote, Pin, PinOff, Trash2, Pencil, Save, X, MessageSquare, Mail,
  Calendar, Phone, Users as UsersIcon, Award, AlertTriangle, Check, Clock, Plus,
} from "lucide-react";

export const NOTE_CATEGORIES = [
  { value: "phone_call",      label: "Phone Call",          icon: Phone,            tone: "navy" },
  { value: "email",           label: "Email",               icon: Mail,             tone: "navy" },
  { value: "meeting",         label: "Meeting",             icon: Calendar,         tone: "navy" },
  { value: "accommodation",   label: "Accommodation",       icon: AlertTriangle,    tone: "amber" },
  { value: "attendance",      label: "Attendance",          icon: Clock,            tone: "amber" },
  { value: "win",             label: "Win / Milestone",     icon: Award,            tone: "emerald" },
  { value: "family_support",  label: "Family / Support",    icon: UsersIcon,        tone: "navy" },
  { value: "other",           label: "Other",               icon: MessageSquare,    tone: "navy" },
];

const toneClass = (tone) => ({
  navy:    "bg-brand-navy/10  text-brand-navy  border-brand-navy/20",
  amber:   "bg-amber-100      text-amber-700   border-amber-200",
  emerald: "bg-emerald-100    text-emerald-700 border-emerald-200",
}[tone] || "bg-slate-100 text-slate-700 border-slate-200");

const CategoryBadge = ({ value }) => {
  const cat = NOTE_CATEGORIES.find((c) => c.value === value) || NOTE_CATEGORIES.at(-1);
  const Icon = cat.icon;
  return (
    <Badge className={`gap-1 ${toneClass(cat.tone)}`} data-testid={`note-category-${cat.value}`}>
      <Icon className="w-3 h-3" /> {cat.label}
    </Badge>
  );
};

const stripHtml = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const NoteEditor = ({ initial = {}, onSave, onCancel, saving }) => {
  const [category, setCategory] = useState(initial.category || "other");
  const [pinned, setPinned] = useState(!!initial.pinned);
  const [followUp, setFollowUp] = useState(initial.follow_up_due || "");
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current && initial.body_html !== undefined) {
      bodyRef.current.innerHTML = initial.body_html || "";
    }
  }, [initial.body_html]);

  const handleSubmit = () => {
    const html = bodyRef.current?.innerHTML || "";
    const text = stripHtml(html);
    if (!text) {
      toast.error("Please write something in the note");
      return;
    }
    onSave({
      category,
      body_html: html,
      body_text: text,
      pinned,
      follow_up_due: followUp || null,
    });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="note-editor">
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-48" data-testid="note-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {NOTE_CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <Calendar className="w-3.5 h-3.5" />
          Follow-up date
          <Input
            type="date"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            className="h-8 w-40 ml-1"
            data-testid="note-followup-input"
          />
        </label>
        <button
          type="button"
          onClick={() => setPinned((p) => !p)}
          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition
            ${pinned ? "bg-brand-gold/20 border-brand-gold/40 text-brand-navy" : "bg-white border-slate-200 text-slate-500 hover:text-slate-700"}`}
          data-testid="note-pin-toggle"
        >
          {pinned ? <Pin className="w-3 h-3 fill-current" /> : <PinOff className="w-3 h-3" />}
          {pinned ? "Pinned" : "Pin"}
        </button>
      </div>
      <div
        ref={bodyRef}
        contentEditable
        suppressContentEditableWarning
        className="min-h-[100px] rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
        data-testid="note-body-editor"
      />
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving} data-testid="note-cancel-btn">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        )}
        <Button type="button" onClick={handleSubmit} disabled={saving} className="btn-primary" data-testid="note-save-btn">
          {saving ? "Saving…" : (<><Save className="w-4 h-4 mr-1" /> Save Note</>)}
        </Button>
      </div>
    </div>
  );
};

const NoteItem = ({ note, onEdit, onDelete, onTogglePin, onMarkFollowUpDone, canModify }) => {
  const created = note.created_at ? new Date(note.created_at).toLocaleString() : "";
  const followUp = note.follow_up_due ? new Date(note.follow_up_due + "T00:00:00").toLocaleDateString() : null;
  const followUpOverdue = note.follow_up_due && !note.follow_up_completed && note.follow_up_due < new Date().toISOString().slice(0, 10);
  return (
    <div
      className={`rounded-xl border bg-white p-4 space-y-2 ${note.pinned ? "border-brand-gold/40 ring-1 ring-brand-gold/30" : "border-slate-200"}`}
      data-testid={`note-item-${note.note_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {note.author_picture ? (
            <img src={note.author_picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-brand-navy text-white flex items-center justify-center text-xs font-bold shrink-0">
              {(note.author_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-sm">{note.author_name}</span>
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-500">{created}</span>
              {note.edited_at && <span className="text-xs text-slate-400 italic">(edited)</span>}
              <CategoryBadge value={note.category} />
              {note.pinned && (
                <Badge className="bg-brand-gold/20 text-brand-navy border-brand-gold/40 gap-1">
                  <Pin className="w-3 h-3 fill-current" /> Pinned
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onTogglePin(note)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title={note.pinned ? "Unpin" : "Pin"} data-testid={`note-pin-${note.note_id}`}>
            {note.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
          </button>
          {canModify && (
            <>
              <button onClick={() => onEdit(note)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Edit" data-testid={`note-edit-${note.note_id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onDelete(note)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Delete" data-testid={`note-delete-${note.note_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      <div
        className="prose prose-sm max-w-none text-slate-700 [&_p]:my-1"
        dangerouslySetInnerHTML={{ __html: note.body_html }}
      />
      {followUp && (
        <div className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs
          ${note.follow_up_completed ? "bg-emerald-50 text-emerald-800" : followUpOverdue ? "bg-rose-50 text-rose-800" : "bg-amber-50 text-amber-800"}`}>
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {note.follow_up_completed ? "Follow-up done" : `Follow-up: ${followUp}`}
            {followUpOverdue && !note.follow_up_completed && <span className="ml-2 font-semibold">Overdue</span>}
          </span>
          {!note.follow_up_completed && (
            <button onClick={() => onMarkFollowUpDone(note)} className="text-xs underline" data-testid={`note-followup-done-${note.note_id}`}>
              Mark done
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ParticipantNotesPanel = ({ participantId, participantName }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [accessError, setAccessError] = useState(null);

  const fetchNotes = useCallback(async () => {
    if (!participantId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/participants/${participantId}/notes`, { credentials: "include" });
      if (r.status === 403) {
        setAccessError("You don't have access to this participant's case notes.");
        setNotes([]);
      } else if (r.ok) {
        setAccessError(null);
        setNotes(await r.json());
      } else {
        setAccessError(null);
        setNotes([]);
      }
    } catch (_) {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [participantId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/participants/${participantId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      toast.success("Case note saved");
      setAdding(false);
      fetchNotes();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (noteId, payload) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/participants/${participantId}/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      toast.success("Note updated");
      setEditingId(null);
      fetchNotes();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm("Delete this note? It will be soft-deleted and recoverable.")) return;
    const r = await fetch(`${API}/admin/participants/${participantId}/notes/${note.note_id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (r.ok) {
      toast.success("Note deleted");
      fetchNotes();
    } else {
      toast.error("Delete failed");
    }
  };

  const handleTogglePin = async (note) => {
    await fetch(`${API}/admin/participants/${participantId}/notes/${note.note_id}/pin`, {
      method: "POST",
      credentials: "include",
    });
    fetchNotes();
  };

  const handleMarkFollowUpDone = async (note) => {
    await handleUpdate(note.note_id, { follow_up_completed: true });
  };

  if (accessError) {
    return (
      <div className="text-sm text-slate-500 p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid="notes-access-error">
        {accessError}
      </div>
    );
  }

  const filtered = filter === "all" ? notes : notes.filter((n) => n.category === filter);
  const pinned = filtered.filter((n) => n.pinned);
  const unpinned = filtered.filter((n) => !n.pinned);

  return (
    <div className="space-y-4" data-testid="participant-notes-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <StickyNote className="w-4 h-4 text-brand-navy" />
          <h4 className="font-heading font-bold text-slate-900">
            Case Notes {participantName ? <span className="text-slate-500 font-normal text-sm">· {participantName}</span> : null}
          </h4>
          <Badge className="bg-slate-100 text-slate-600 border-slate-200">{notes.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="notes-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All categories</SelectItem>
              {NOTE_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!adding && (
            <Button onClick={() => setAdding(true)} className="btn-primary gap-1 h-8" data-testid="add-note-btn">
              <Plus className="w-4 h-4" /> Add note
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-400 italic">
        🔒 These notes are admin/instructor-only — never visible to the participant.
      </p>

      {adding && (
        <NoteEditor
          onSave={handleCreate}
          onCancel={() => setAdding(false)}
          saving={saving}
        />
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Loading notes…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm" data-testid="notes-empty">
          {notes.length === 0 ? "No case notes yet. Add one above." : "No notes match this filter."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="notes-list">
          {pinned.map((n) => (
            editingId === n.note_id ? (
              <NoteEditor key={n.note_id} initial={n} onSave={(p) => handleUpdate(n.note_id, p)} onCancel={() => setEditingId(null)} saving={saving} />
            ) : (
              <NoteItem
                key={n.note_id}
                note={n}
                onEdit={(nt) => setEditingId(nt.note_id)}
                onDelete={handleDelete}
                onTogglePin={handleTogglePin}
                onMarkFollowUpDone={handleMarkFollowUpDone}
                canModify={user?.role === "admin" || n.author_user_id === user?.user_id}
              />
            )
          ))}
          {unpinned.map((n) => (
            editingId === n.note_id ? (
              <NoteEditor key={n.note_id} initial={n} onSave={(p) => handleUpdate(n.note_id, p)} onCancel={() => setEditingId(null)} saving={saving} />
            ) : (
              <NoteItem
                key={n.note_id}
                note={n}
                onEdit={(nt) => setEditingId(nt.note_id)}
                onDelete={handleDelete}
                onTogglePin={handleTogglePin}
                onMarkFollowUpDone={handleMarkFollowUpDone}
                canModify={user?.role === "admin" || n.author_user_id === user?.user_id}
              />
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default ParticipantNotesPanel;
